import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { IonicModule } from '@ionic/angular';
import { firstValueFrom } from 'rxjs';
import { DataService } from '../../data.service';

/**
 *  ADMIN › ELLENŐRZÉS › JEGY NÉLKÜLI TANTÁRGYAK (backend/jegy-ellenorzes.js).
 *
 *  Ellenőrző nézet (nem osztályfőnöki feladat): melyik osztályban/csoportban melyik tantárgyból
 *  kinek nincs még évközi jegye, a tanárral és a megtartott óraszámmal. Tanáronkénti nézetben ugyanez
 *  a tanár szerint – kinek kell szólni.
 */
@Component({
  selector: 'app-admin-jegy-ellenorzes',
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule],
  templateUrl: './jegy-ellenorzes.component.html',
  styleUrls: ['./jegy-ellenorzes.component.scss'],
})
export class AdminJegyEllenorzesComponent implements OnInit {
  intezmeny = '';
  betolt = false;
  hiba = '';
  adat: any = null;
  osztalyok: any[] = [];

  idoszak = 'ev';
  tol = '';
  ig = '';
  osztaly = '';
  csakSenkinek = false;
  minOra = 4;
  nezet: 'tantargy' | 'tanar' = 'tantargy';
  nyitva: Record<string, boolean> = {};

  constructor(private http: HttpClient, private data: DataService) {}

  async ngOnInit() {
    const sajat: any = await this.data.getFromStorage('sajatIntezmeny').catch(() => null);
    this.intezmeny = String(sajat?.intezmenyId || '');
    if (!this.intezmeny) { this.hiba = 'Nincs kiválasztott intézmény.'; return; }
    this.osztalyok = await firstValueFrom(this.http.get<any[]>(`/api/jegy-ellenorzes/osztalyok/${this.intezmeny}`)).catch(() => []);
    await this.toltes();
  }

  async toltes() {
    if (this.idoszak === 'egyeni' && (!this.tol || !this.ig)) return;
    this.betolt = true; this.hiba = '';
    try {
      const params: any = { idoszak: this.idoszak };
      if (this.idoszak === 'egyeni') { params.tol = this.tol; params.ig = this.ig; }
      if (this.osztaly) params.osztaly = this.osztaly;
      this.adat = await firstValueFrom(this.http.get(`/api/jegy-ellenorzes/kimutatas/${this.intezmeny}`, { params }));
      this.nyitva = {};
    } catch (e: any) {
      this.hiba = e?.error?.error || 'Nem sikerült betölteni a kimutatást.';
    } finally { this.betolt = false; }
  }

  get sorok(): any[] {
    return (this.adat?.egysegek || []).filter((e: any) => (!this.csakSenkinek || e.senkinek) && e.orak >= (Number(this.minOra) || 0));
  }

  /** Tanáronként: minden egység annál a tanárnál, aki órát tartott benne. */
  get tanaronkent(): { nev: string; egysegek: any[]; jegyNelkul: number }[] {
    const m = new Map<string, { nev: string; egysegek: any[]; jegyNelkul: number }>();
    for (const e of this.sorok) {
      for (const t of e.tanarok || []) {
        if (!m.has(t.adid)) m.set(t.adid, { nev: t.nev, egysegek: [], jegyNelkul: 0 });
        const x = m.get(t.adid)!;
        x.egysegek.push({ ...e, sajatOra: t.orak });
        x.jegyNelkul += e.jegy_nelkul;
      }
    }
    return [...m.values()].sort((a, b) => b.egysegek.length - a.egysegek.length || a.nev.localeCompare(b.nev, 'hu'));
  }

  senkinekDb() { return this.sorok.filter((e: any) => e.senkinek).length; }
  tanarSzoveg(e: any) { return (e.tanarok || []).map((t: any) => `${t.nev} (${t.orak})`).join(', '); }
  kulcs(e: any) { return `${e.csoport_id || e.osztaly_id}|${e.tantargy_id}`; }
  valt(e: any) { const k = this.kulcs(e); this.nyitva[k] = !this.nyitva[k]; }

  csv() {
    const cella = (v: any) => { const s = String(v ?? ''); return /[;"\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const ki = [['Osztály/csoport', 'Tantárgy', 'Tanár(ok) (óraszám)', 'Megtartott óra', 'Jegy nélkül', 'Létszám', 'Utolsó jegy', 'Jegy nélküli tanulók'].join(';')];
    for (const e of this.sorok) {
      ki.push([e.egyseg, e.tantargy, this.tanarSzoveg(e), e.orak, e.jegy_nelkul, e.letszam, e.utolso_jegy || '',
        (e.tanulok || []).map((t: any) => `${t.nev}${t.osztaly ? ' (' + t.osztaly + ')' : ''}`).join(', ')].map(cella).join(';'));
    }
    const blob = new Blob([`﻿${ki.join('\r\n')}\r\n`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `jegy-nelkuli-tantargyak-${this.adat?.idoszak?.tol || ''}_${this.adat?.idoszak?.ig || ''}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}
